import { env } from '@/lib/env'

/** The customer's signing link. Everything else in the app is the dealership's own PWA. */
const PUBLIC_SIGNING_PREFIX = '/s/'

/** One automatic reload per failing file per session, so a broken build shows its error. */
const RELOADED_FOR_PREFIX = 'dealerdrive.reloaded_for:'

/**
 * Reload the page once the worker that served it has been replaced by a newer build's.
 *
 * A deploy renames every hashed chunk. `autoUpdate` puts `skipWaiting` and `clientsClaim` in the
 * worker, so the new one takes over the moment it has cached the new build and, in the same
 * step, drops the old build's files from the cache. The page that is open is still the old
 * build; the next screen it has not yet loaded asks for a file that is nowhere: not in the
 * cache, and not on the server. That screen's component fails to load and the error boundary
 * takes over. An installed app has no address bar, so on a phone the only way out was to close
 * the app and open it again.
 *
 * `vite-plugin-pwa`'s own register script reloads on exactly this event, and it was what made
 * `autoUpdate` safe; registering by hand had dropped it.
 */
export function reloadWhenWorkerReplaced(
  workers: Pick<ServiceWorkerContainer, 'controller' | 'addEventListener'>,
  reload: () => void,
): void {
  // On a first visit the page starts uncontrolled and `clientsClaim` takes it over: a change of
  // controller, but the build the page was served from, and nothing to reload for.
  let controlled = workers.controller !== null
  workers.addEventListener('controllerchange', () => {
    if (controlled) reload()
    controlled = true
  })
}

/**
 * Reload when a chunk or its stylesheet cannot be loaded, once per failing file per session.
 *
 * A page that outlives its build (a service worker updated underneath it, a shell held in an
 * HTTP cache) points at files that no longer exist. TanStack Router reloads for a route
 * component whose module cannot be fetched; this covers what it does not — a stylesheet, an
 * error message it does not recognise — with the same rule. A second failure of the same file
 * after a reload is a build that is genuinely broken, and is left to show its error rather than
 * reload forever.
 */
export function reloadOnChunkFailure(target: Window, reload: () => void): void {
  target.addEventListener('vite:preloadError', (event) => {
    const key = RELOADED_FOR_PREFIX + event.payload.message
    try {
      if (target.sessionStorage.getItem(key) !== null) return
      target.sessionStorage.setItem(key, '1')
    } catch {
      // No storage means no way to stop a loop. Let the error show instead.
      return
    }
    reload()
  })
}

/**
 * Register the service worker `vite-plugin-pwa` generates — by hand, because the plugin's own
 * injection has no way to skip a route.
 *
 * The worker is the app's: it precaches the shell and keeps API responses for five minutes. A
 * customer opening a one-off signing link gets neither — no worker installed on a phone that
 * will never come back, and nothing of theirs left in a cache on it.
 */
export function registerServiceWorker(): void {
  if (env.DEV || !('serviceWorker' in navigator)) return
  if (window.location.pathname.startsWith(PUBLIC_SIGNING_PREFIX)) return
  reloadWhenWorkerReplaced(navigator.serviceWorker, () => window.location.reload())
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js', { scope: '/' })
  })
}
